// Answer-first expansions for pages already ranking just off page one.
//
// Each entry targets the exact query the page already ranks for (positions
// 32-54 in Semrush's US index) with a short, answer-first block placed on that
// same URL, so the existing page gets deeper coverage instead of a new
// competing page. Visible copy only — no extra JSON-LD, so pages keep a single
// FAQPage block and there is no duplicate-schema risk.

export interface ExpansionItem {
  q: string;
  a: string;
}

export interface PageExpansion {
  heading: string;
  intro: string;
  items: ExpansionItem[];
}

export const PAGE_EXPANSIONS: Record<string, PageExpansion> = {
  // Brand/hub intent only. Roster, stat-lookup and "how many Pals" intents were
  // moved to /pals so Google stops matching the homepage to database queries it
  // can only answer at position 60+.
  "/": {
    heading: "What Pal Stars covers",
    intro:
      "Pal Stars is a Palworld wiki and Palworld TCG data site: the Pal database, the calculators and the daily card price index each live on their own page, so a search lands on the tool that answers it instead of a hub page.",
    items: [
      {
        q: "What is Pal Stars?",
        a: "A free Palworld reference site with two halves: the in-game wiki (Pal database, breeding, damage, mounts, capture rates, maps, guides) and the Palworld trading card side (card database, daily price chart, price index, bulk ordering). Each dataset has a dedicated page rather than being stacked onto one long homepage.",
      },
      {
        q: "Where do I start on Pal Stars?",
        a: "For Pal stats open the Pal database. For a pairing use the breeding calculator, for combat maths the damage calculator, for travel the mount speed table. For cards start at the card database or the daily price index.",
      },
      {
        q: "Is Pal Stars up to date with the latest Palworld patch?",
        a: "Yes. Pal, breeding and item data are refreshed against the current release, and card prices resync from marketplace data twice a day with the last sync time shown on every price page.",
      },
    ],
  },
  // Roster/stat-lookup intent belongs to the Pal database page.
  "/pals": {
    heading: "Palworld database, Pal stats and roster answers",
    intro:
      "The Pal Stars Palworld database indexes every officially released Pal with full stats, elements, work suitability, mount speed, spawn region and breeding parents on one page each, so a stat lookup takes one search rather than a wiki crawl.",
    items: [
      {
        q: "What is the Palworld database on Pal Stars?",
        a: "It is a searchable index of every officially released Pal, each with its own page carrying attack, defence, HP, work suitability, element, partner skill, mount speed, spawn area and confirmed breeding parents. Search by name, element, work trait or mount speed, and every stat links through to the breeding calculator, damage calculator and interactive map.",
      },
      {
        q: "Where can I look up Palworld Pal stats?",
        a: "Open any Pal page from this database. Each page lists base attack and defence, effective DPS, food and stamina, work suitability levels, mount and glide speed where applicable, and the passive skills that change those numbers, all pulled from the same dataset the calculators use so figures never disagree between pages.",
      },
      {
        q: "How many Pals are in Palworld?",
        a: "The database tracks the full officially released roster including subspecies and boss variants, and the Pal count shown on this page is derived from the dataset itself rather than typed in, so it stays correct after every content update.",
      },
      {
        q: "How are Pals ranked on Pal Stars?",
        a: "Pals are ranked separately for combat DPS, base work output and mount speed, because the best raid Pal is rarely the best worker. Use the damage calculator for combat order, the mount speed table for travel and the tier list for an overall read.",
      },
    ],
  },

  "/damage": {
    heading: "Palworld damage from Pals multiplier explained",
    intro:
      "Damage from Pals is a separate multiplier from your own weapon damage: it scales the damage your active Pal deals, stacks multiplicatively with element matchup and passive skills, and is the single biggest lever on raid time-to-kill.",
    items: [
      {
        q: "What does the damage from Pals multiplier do in Palworld?",
        a: "It multiplies the damage your Pals deal, leaving player weapon damage untouched. On a server or in world settings it is a slider; in normal play it is raised by passives, condensation star ranking and element advantage. Because it multiplies rather than adds, a 1.5x Pal damage setting on top of a 2x element advantage produces 3x, not 2.5x.",
      },
      {
        q: "How do I calculate Palworld Pal damage?",
        a: "Start from the Pal's base attack, apply the element multiplier against the target, then apply passive skill multipliers (Ferocious, Musclehead, Legendary), then condensation and star rank, then the Pal damage world setting. The calculator on this page runs that chain for every Pal so you can compare effective DPS and boss time-to-kill directly.",
      },
      {
        q: "What is the best Pal damage multiplier setting?",
        a: "For a normal solo world 1.0 keeps boss fights meaningful; 2.0 to 3.0 is the common co-op setting because boss HP scales with player count. Raising Pal damage does not raise incoming damage, so it shortens raids without making them safer for you.",
      },
      {
        q: "Does element advantage stack with the Pal damage multiplier?",
        a: "Yes, and multiplicatively. Element advantage is applied per hit against the target's element, then the world multiplier applies to the result — which is why matching element to boss is worth more than any single passive skill.",
      },
    ],
  },
  "/guides": {
    heading: "Palworld NPC and Pal critic answers",
    intro:
      "Palworld's world NPCs range from merchants and Pal traders to the arrogant Pal critic who rates your Pals, and each one is covered in a guide with location, dialogue outcome and what it is actually worth doing.",
    items: [
      {
        q: "Who is the arrogant Pal critic in Palworld?",
        a: "The arrogant Pal critic is a world NPC who inspects a Pal you present and delivers a graded verdict on it. The rating reflects the Pal's stats and star rank rather than anything hidden, so a condensed, high-passive Pal draws the top response while a freshly caught one is dismissed. Nothing is lost by showing a Pal, so it is a free read on how a Pal compares.",
      },
      {
        q: "Where do I find Palworld NPC merchants and traders?",
        a: "Wandering merchants sit in the small settlements and at tower approaches, while Pal traders and the black-marketeer types appear in fixed camps. The merchant guide lists each location with fast travel points and stock, and the interactive map marks them alongside spawns.",
      },
      {
        q: "Which Palworld guides cover NPCs and world encounters?",
        a: "The merchant guide covers buying and selling, the boss guide covers Alpha and tower encounters, and the farming guide covers the gold loops NPC selling feeds. Each guide contains five articles plus its own FAQ.",
      },
    ],
  },
  "/guides/farming": {
    heading: "Palworld farming quick answers",
    intro:
      "Farming in Palworld splits into three separate economies — food and cake production, resource nodes, and gold — and the fastest route through each is different. These answers cover what to farm first at every stage.",
    items: [
      {
        q: "What is the best thing to farm in Palworld?",
        a: "Early on, berries and wood, because they gate everything else. Mid-game the priority is Cake — it unlocks breeding, which unlocks every strong Pal you would otherwise hunt for. Late game the bottleneck is gold and Ancient Technology Points, so the loop becomes Alpha resales and raid drops rather than resources.",
      },
      {
        q: "How do you farm efficiently in Palworld?",
        a: "Split bases by job: one dedicated food and cake base with Watering, Planting, Gathering and Kindling cover, one resource base sited on overlapping ore and coal nodes, and one breeding base. A single base trying to do all three starves its own worker slots and is the most common reason production stalls.",
      },
      {
        q: "What is the fastest way to farm gold in Palworld?",
        a: "Sell crafted goods and surplus Pals through merchants on a daily loop rather than selling raw materials, then add Alpha resales once you can clear them quickly. The gold-farming article on this page gives the exact daily rotation and the AFK gold setup for overnight income.",
      },
      {
        q: "How do you farm Cake in Palworld?",
        a: "Cake needs Flour, Milk, Egg, Honey and Red Berries, so the loop is a wheat plot with a Mill, a ranch with Milk and Egg producers, and a Honey Pal, all in one base with a Kindling worker on the Cooking Pot. Once running, it is fully passive and feeds the Breeding Farm indefinitely.",
      },
    ],
  },
  "/guides/direhowl": {
    heading: "Direhowl quick answers",
    intro:
      "Direhowl is the early ground mount most players ride first: fast, cheap to saddle and available before you can catch anything stronger.",
    items: [
      {
        q: "Where do you find Direhowl in Palworld?",
        a: "Direhowl spawns in the open grassland and hill areas of the starting region, and it appears both day and night. It is aggressive on sight, so bring a ranged option and spheres rated for its level rather than trying to melee it down.",
      },
      {
        q: "Is Direhowl a good mount in Palworld?",
        a: "Yes, as an early mount. Its ground speed beats anything else available at that stage and its saddle unlocks cheaply, which makes it the standard first ride. It is outclassed later by dedicated high-speed ground mounts, so treat it as a stepping stone rather than an endgame pick.",
      },
      {
        q: "How do you get the Direhowl saddle?",
        a: "Unlock the saddle in the technology tree at its listed level, then craft it at a workbench from leather, fibre and ingot. The saddle unlock levels table lists every mount saddle and the level it becomes available.",
      },
    ],
  },
  "/pals/bastigor": {
    heading: "Bastigor build answers",
    intro:
      "A Bastigor build comes down to three choices: the passives you breed into it, the star rank you condense it to, and whether you are using it as a raid damage Pal or a base worker.",
    items: [
      {
        q: "What is the best Bastigor build in Palworld?",
        a: "For combat, stack the attack passives — Ferocious, Musclehead and Legendary where available — then condense to four stars before you level, because condensation multiplies the base you are levelling. For base work, ignore attack passives entirely and breed for work speed and the Serious trait instead.",
      },
      {
        q: "What passives should Bastigor have?",
        a: "Attack builds want Musclehead and Ferocious together, plus one defensive or stamina passive to keep it alive in raids. Avoid mixing work-speed passives into a combat build: the four slots are the whole budget, and a split build underperforms both roles.",
      },
      {
        q: "How do you get Bastigor passives?",
        a: "Breed it with parents carrying the passives you want — inherited passives come from the parent pool, so two parents each carrying two target passives is the reliable route. The breeding calculator lists the parent combinations, and the passive inheritance guide covers the exact odds.",
      },
    ],
  },
};

export function pageExpansion(path: string): PageExpansion | undefined {
  return PAGE_EXPANSIONS[path];
}
